import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { map } from 'rxjs';

import { IconButton } from '@client:common/button';
import { Ripple } from '@client:common/effects/ripple';
import { Icon } from '@client:common/icon';
import { Dialog } from '@client:common/overlay/dialog';
import { Notification } from '@client:common/overlay/notification';
import { ProgressIndicator } from '@client:common/overlay/progress-indicator';
import { GlobalStore } from '@client:common/global-store';

import { LoginForm } from './views/login-form/LoginForm';
import { SessionManagerService } from './services/SessionManagerService';

import './SessionManager.dark.scss';
import './SessionManager.light.scss';

/**
 * This component handles logging in and logging out. It delegates all other
 * account related tasks to other modules using routing
 *
 * @returns
 */
export function SessionManager() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const onShowLoginForm = () => {
    const dialogRef = Dialog.create(
      <>
        <IconButton
          className='login-dialog__close'
          style='basic'
          color='primary'
          onClick={() => {
            dialogRef.close();
          }}
        >
          <Ripple fixed duration={1000} />
          <Icon icon='close' />
        </IconButton>
        <LoginForm
          onLogin={(email, password) => {
            const sessionManagerService = SessionManagerService.getInstance();

            ProgressIndicator.show();
            sessionManagerService
              .signIn(email, password)
              .then((authenticatedAccount) => {
                const globalStore = GlobalStore.getInstance();

                globalStore.update({
                  authenticatedAccount
                });
                setIsLoggedIn(true);
                dialogRef.close();

                navigate('/');
              })
              .catch(() => {
                Notification.open(
                  'Email or password is incorrect, please try again'
                );
              })
              .finally(() => {
                ProgressIndicator.hide();
              });
          }}
        />
      </>
    )
      .addClassName('login-dialog')
      .open();
  };
  const onLogout = () => {
    const sessionManagerService = SessionManagerService.getInstance();

    ProgressIndicator.show();
    sessionManagerService
      .signOut()
      .then(() => {
        const globalStore = GlobalStore.getInstance();

        globalStore.update({
          authenticatedAccount: null
        });
        setIsLoggedIn(false);
        navigate('/');
      })
      .catch(() => {
        Notification.open('Logging out failed, please try again');
      })
      .finally(() => {
        ProgressIndicator.hide();
      });
  };

  useEffect(() => {
    const sessionManagerService = SessionManagerService.getInstance();
    const globalStore = GlobalStore.getInstance();
    const subscription = globalStore
      .select('authenticatedAccount')
      .pipe(
        map(
          (authenticatedAccount) =>
            authenticatedAccount && authenticatedAccount.isVerified
        )
      )
      .subscribe({
        next: setIsLoggedIn
      });

    sessionManagerService
      .restoreSession()
      .then((authenticatedAccount) => {
        globalStore.update({
          authenticatedAccount
        });
        setIsLoggedIn(true);
      })
      .catch((reason) => {
        if (reason !== 'no current user') {
          console.warn('Unable to restore previous session: reason is', reason);
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <div className='session-manager'>
      {!isLoggedIn ? (
        <IconButton
          className='session-manager__action show-login-dialog'
          style='raised'
          color='primary'
          onClick={onShowLoginForm}
        >
          <Ripple />
          <Icon icon='login' />
          <span className='session-manager__action-text'>LOG IN</span>
        </IconButton>
      ) : (
        <IconButton
          className='session-manager__action trigger-logout'
          style='stroked'
          color='primary'
          onClick={onLogout}
        >
          <Ripple />
          <Icon icon='logout' />
          <span className='session-manager__action-text'>LOG OUT</span>
        </IconButton>
      )}
    </div>
  );
}
